import { Link, useLocation } from "react-router-dom";
import { Home, Compass, CalendarDays, Route, Heart, User, Shield, Footprints, Play } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { resolveNavConfig } from "@/lib/layout";
import { InstallPrompt } from "@/components/InstallPrompt";

const NAV_META = {
  home: { icon: Home, testid: "nav-home" },
  explore: { icon: Compass, testid: "nav-explore" },
  events: { icon: CalendarDays, testid: "nav-events" },
  trails: { icon: Footprints, testid: "nav-trails" },
  videos: { icon: Play, testid: "nav-videos" },
  itinerary: { icon: Route, testid: "nav-itinerary" },
  saved: { icon: Heart, testid: "nav-saved" },
};

export function Layout({ children }) {
  const location = useLocation();
  const { user } = useAuth();
  const { settings } = useSettings();
  const isActive = (to) => (to === "/" ? location.pathname === "/" : location.pathname.startsWith(to));

  const navItems = resolveNavConfig(settings)
    .filter((n) => n.visible !== false && NAV_META[n.key])
    .map((n) => ({ to: n.path, label: n.label, ...NAV_META[n.key] }));

  return (
    <div className="min-h-screen pb-20 md:pb-0">
      {/* Top nav */}
      <header className="glass sticky top-0 z-40 border-b border-border/40">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 md:px-8">
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <img src="/icon-mark.png" alt="CSRAGo" className="h-9 w-9 rounded-full object-cover" />
            <span className="font-serif text-2xl font-bold tracking-tight text-foreground">
              CSRAGo
            </span>
          </Link>

          <nav className="hidden items-center gap-1 md:flex">
            {navItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                data-testid={`${item.testid}-desktop`}
                className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                  isActive(item.to)
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            {user && (user.role === "admin" || user.role === "editor") && (
              <Link
                to="/admin"
                data-testid="nav-admin"
                className="hidden items-center gap-1.5 rounded-full bg-secondary px-4 py-2 text-sm font-medium text-secondary-foreground transition-transform hover:scale-105 md:flex"
              >
                <Shield size={15} /> Admin
              </Link>
            )}
            <Link
              to={user ? "/profile" : "/auth"}
              data-testid="nav-profile"
              className="flex items-center gap-2 rounded-full border border-border bg-card px-3 py-2 text-sm font-medium transition-colors hover:border-primary"
            >
              <User size={16} />
              <span className="hidden sm:inline">
                {user ? user.name?.split(" ")[0] : "Sign in"}
              </span>
            </Link>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl">{children}</main>

      {/* Mobile bottom tab bar */}
      <nav className="glass fixed bottom-0 left-0 right-0 z-40 flex items-stretch justify-around border-t border-border/40 px-1 pt-1 pb-[max(0.375rem,env(safe-area-inset-bottom))] md:hidden">
        {navItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.to);
          return (
            <Link
              key={item.to}
              to={item.to}
              data-testid={`${item.testid}-mobile`}
              className={`flex min-h-[48px] flex-1 flex-col items-center justify-center gap-1 rounded-xl px-1 py-1.5 text-[11px] font-medium transition-colors active:bg-primary/10 ${
                active ? "text-primary" : "text-muted-foreground"
              }`}
            >
              <Icon size={22} className={active ? "fill-primary/15" : ""} />
              {item.label}
            </Link>
          );
        })}
      </nav>

      <InstallPrompt />
    </div>
  );
}
